import { useSyncExternalStore } from 'react';

import type { Api, MeOut, TenantBrief, UserBrief } from '@/lib/api/endpoints';

import type { Permission, Role } from './permissions';
import { hasAnyRole, hasPermission, isStaff, permissionsFor } from './permissions';

/**
 * Who is logged in, which tenant they are in, and what that lets them do
 * (BP-01 §6 "Shared `lib/`: `auth/` — session store").
 *
 * The store holds exactly what `GET /me` returns. The *credential* is never
 * here: in cookie mode it is an HttpOnly cookie the browser holds and this code
 * cannot read (R5), and in bearer mode it lives in the `TokenStore` the fetcher
 * is built with (R6). So "log out" means "tell the api, then forget who we are".
 */

/** The session as the app sees it. */
export interface SessionState {
  user: UserBrief | null;
  roles: readonly string[];
  tenant: TenantBrief | null;
  /**
   * BP-19 rule 1, already resolved by `GET /me`: this person's choice, else the
   * tenant default, else `en`. `null` while nobody is signed in — the shell then
   * has only this browser's remembered choice to go on.
   */
  locale: string | null;
}

/** Nobody is logged in. */
export const ANONYMOUS: SessionState = Object.freeze({
  user: null,
  roles: Object.freeze([]),
  tenant: null,
  locale: null,
});

/** `GET /me` as a `SessionState`. */
export function stateFromMe(me: MeOut): SessionState {
  return { user: me.user, roles: me.roles, tenant: me.tenant, locale: me.locale };
}

/** A minimal observable store, shaped for React's `useSyncExternalStore`. */
export class SessionStore {
  #state: SessionState = ANONYMOUS;
  readonly #listeners = new Set<() => void>();

  /** The current session. Stable identity between changes, as the hook requires. */
  readonly get = (): SessionState => this.#state;

  /** Subscribe to changes; the returned function unsubscribes. */
  readonly subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  /** Replace the session and notify. */
  readonly set = (next: SessionState): void => {
    this.#state = next;
    for (const listener of this.#listeners) {
      listener();
    }
  };

  /** Forget who is logged in. */
  readonly clear = (): void => {
    this.set(ANONYMOUS);
  };
}

/** True when a user is logged in. */
export function isAuthenticated(state: SessionState): boolean {
  return state.user !== null;
}

/** R16: `tenant.status = readonly`, which is what the `ReadOnlyBanner` shows. */
export function isReadOnly(state: SessionState): boolean {
  return state.tenant?.status === 'readonly';
}

/** Permission helpers derived from the session's roles (R8). */
export interface SessionAbilities {
  authenticated: boolean;
  readOnly: boolean;
  staff: boolean;
  permissions: ReadonlySet<Permission>;
  can: (permission: Permission) => boolean;
  isAny: (...roles: Role[]) => boolean;
}

/** Everything a screen needs to decide what to show, derived from one state. */
export function abilitiesOf(state: SessionState): SessionAbilities {
  return {
    authenticated: isAuthenticated(state),
    readOnly: isReadOnly(state),
    staff: isStaff(state.roles),
    permissions: permissionsFor(state.roles),
    can: (permission) => hasPermission(state.roles, permission),
    isAny: (...roles) => hasAnyRole(state.roles, roles),
  };
}

/** Subscribe a component to `store`. */
export function useSession(store: SessionStore): SessionState {
  return useSyncExternalStore(store.subscribe, store.get);
}

/**
 * Log out: revoke the session server-side (`POST /auth/logout`, §5), then forget
 * it locally.
 *
 * A failed call is swallowed on purpose. The button's promise to the user is
 * "this browser forgets you", and that must hold when the network is down or the
 * session has already expired; the server-side row is expired or purged anyway
 * (`session_purge`, §8).
 */
export async function logout(api: Pick<Api, 'logout'>, store: SessionStore): Promise<void> {
  await api.logout().catch(() => undefined);
  store.clear();
}
